// src/views/Whiteboard.jsx
// THE WHITEBOARD — Reece's misc notes (10/2/26): the pieces both windows onto
// the board render — the bottom-left bubble (src/views/WhiteboardDock.jsx)
// and the Command Center panel (WhiteboardPanel below, #cc-whiteboard, right
// under Follow-ups — "see them on that main screen"). The notes live in ONE
// store per person (src/whiteboard.js), so a note added in one is in the
// other at once; neither keeps a copy of its own.
//
// Free text, NOT a to-do list: no checkboxes, due dates or reminders, and
// nothing here feeds the Follow-ups bell. A note is TEXT ONLY — rendered as a
// React text child (white-space: pre-wrap), never as HTML.
//
// Editing a note: click its text (or Edit). The box is an open edit in the
// store (state.edits — so the bubble and the panel show the same box): it
// becomes the note ~0.8 s after the last keystroke (that one row goes out)
// and on leaving the box. Esc drops the edit — before that pause, the note is
// left exactly as it was (no "edited" date, no write); after it, the pre-edit
// text is written back. The bubble's Esc handler sees the editor and leaves
// the panel open. An emptied note is never saved (Erase is how a note goes).
// Over WB_MAX characters the edit is NOT saved and NOT thrown away: it says
// so, stays open — across minimizing, other screens and sign-out (it is kept
// on this device) — until it's shortened or Esc'd.
//
// Erase is a soft delete: "Erased — Undo" for a few seconds, then the note
// sits under "Recently erased (N)" with Restore.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { C } from '../theme';
import { WB_MAX, WB_SQL_FILE, getWhiteboardStore, useWhiteboard, whenLabel, relTime } from '../whiteboard';

const FONT = "'Mulish',system-ui,sans-serif";
const ERASED_SHOWN = 30;          // most recent erased notes listed
const UNDO_MS = 8000;
const fmtN = function (n) { return Number(n).toLocaleString("en-US"); };
const addKey = function (e) { return e.key === "Enter" && (e.ctrlKey || e.metaKey) && !(e.nativeEvent && e.nativeEvent.isComposing); };
// Grow with the text (jsdom / a hidden box report 0 — leave the CSS height).
function autosize(el, max) {
  if (!el) return;
  el.style.height = "auto";
  const h = el.scrollHeight || 0;
  el.style.height = h ? Math.min(h, max) + "px" : "";
}
const focusEl = function (el) { if (!el) return; try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } };
// "added 5m ago" / "edited yesterday" stay current while on screen.
function useNow() {
  const [now, setNow] = useState(function () { return Date.now(); });
  useEffect(function () { const t = setInterval(function () { setNow(Date.now()); }, 60000); return function () { clearInterval(t); }; }, []);
  return now;
}
const actBtn = { border: "1px solid " + C.border, background: "transparent", color: C.textMuted, borderRadius: 7, fontSize: 11, fontWeight: 700, padding: "3px 9px", minHeight: 26, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" };
const linkBtn = { border: "none", background: "transparent", padding: 0, color: C.accentLight, fontSize: "inherit", fontWeight: 800, cursor: "pointer", fontFamily: FONT, textDecoration: "underline" };

// ═══════ Add a note ═══════
// The half-typed note is the store's `draft`, so it survives the bubble
// hiding (going to the Command Center shows it in the panel's box).
export function WhiteboardAdd({ staff, compact, inputRef }) {
  const store = getWhiteboardStore(staff);
  const draft = useWhiteboard(staff, function (s) { return s.draft; });
  const ownRef = useRef(null);
  const taRef = inputRef || ownRef;
  const max = compact ? 160 : 220;
  useEffect(function () { autosize(taRef.current, max); }, [draft, taRef, max]);
  const len = draft.trim().length;
  const tooLong = len > WB_MAX;
  const can = len > 0 && !tooLong;
  const add = function () {
    if (!can) return;
    const r = store.add(draft);
    if (r && r.ok) store.setDraft("");
    focusEl(taRef.current);
  };
  return (<div data-wb-add="">
    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", background: C.card, border: "1px solid " + (tooLong ? C.red : C.border), borderRadius: 12, padding: "5px 5px 5px 3px" }}>
      <textarea ref={taRef} value={draft} rows={2}
        onChange={function (e) { store.setDraft(e.target.value); }}
        onKeyDown={function (e) { if (addKey(e)) { e.preventDefault(); add(); } }}
        placeholder="Jot something down…" aria-label="Add a whiteboard note" aria-invalid={tooLong || undefined}
        style={{ flex: 1, minWidth: 0, resize: "none", border: "none", outline: "none", background: "transparent", boxShadow: "none", color: C.text, fontSize: compact ? 13.5 : 14, lineHeight: 1.45, fontFamily: FONT, padding: "7px 8px", minHeight: 44, maxHeight: max, overflowY: "auto", boxSizing: "border-box" }} />
      <button type="button" onClick={add} disabled={!can} data-wb-add-btn=""
        title={tooLong ? "Too long — shorten it first" : "Add it to the board (Ctrl+Enter)"}
        style={{ flexShrink: 0, height: 34, padding: "0 14px", borderRadius: 9, border: "none", background: can ? C.navy : C.surfaceAlt, color: can ? "#fff" : C.textDim, fontSize: 12.5, fontWeight: 800, cursor: can ? "pointer" : "default", fontFamily: FONT }}>Add</button>
    </div>
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 10.5, color: C.textDim, marginTop: 4, padding: "0 4px" }}>
      <span>Ctrl+Enter (⌘+Enter on a Mac) adds it · Enter is a new line</span>
      {len > WB_MAX - 500 && <span style={{ marginLeft: "auto", fontWeight: 700, color: tooLong ? C.red : C.textDim }}>{fmtN(len)} / {fmtN(WB_MAX)}</span>}
    </div>
    {tooLong && <div role="alert" data-wb-too-long="" style={{ fontSize: 11.5, color: C.red, fontWeight: 700, marginTop: 4, padding: "0 4px", lineHeight: 1.45 }}>
      Too long to add — a note holds up to {fmtN(WB_MAX)} characters and this is {fmtN(len)}. Shorten it or split it into two notes; nothing is cut.
    </div>}
  </div>);
}

// ═══════ One note ═══════
function NoteCard({ staff, note, failed, compact, now, onErase }) {
  const store = getWhiteboardStore(staff);
  const id = note.id;
  // The open edit lives in the store — the bubble and the Command Center show
  // the same box, and it outlives this card (see the header).
  const pend = useWhiteboard(staff, function (s) { return s.edits[id] || null; });
  const editing = !!pend;
  const draft = pend ? pend.text : "";
  const shownRef = useRef(false); shownRef.current = editing;
  const focusRef = useRef(false);      // this card opened the box → the cursor goes in
  const aliveRef = useRef(true);
  const taRef = useRef(null);
  const textRef = useRef(null);
  const max = compact ? 260 : 320;

  const startEdit = function () {
    if (editing) { focusEl(taRef.current); return; }
    focusRef.current = true;
    if (!store.startEdit(id)) focusRef.current = false;
  };
  const backToText = function () { setTimeout(function () { focusEl(textRef.current); }, 0); };
  // Leave the box (blur), Done or Ctrl+Enter. Over the limit it stays open
  // and says so; emptied, the note keeps its pre-edit text (Erase removes a note).
  const finish = function (viaKey) {
    const r = store.finishEdit(id);
    if (r === "too_long" || r === "none") return;
    if (viaKey) backToText();
  };
  // Esc: drop this edit (see the header).
  const cancel = function (viaKey) { if (store.cancelEdit(id) && viaKey) backToText(); };
  const cancelRef = useRef(cancel); cancelRef.current = cancel;
  const onChange = function (e) { store.typeEdit(id, e.target.value); };
  const onKey = function (e) {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); cancel(true); return; }
    if (addKey(e)) { e.preventDefault(); finish(true); }
  };
  const keepFocus = function (e) { if (editing) e.preventDefault(); };

  useEffect(function () {
    if (!editing) return undefined;
    const ta = taRef.current;
    if (!ta) return undefined;
    if (focusRef.current) {
      focusRef.current = false;
      focusEl(ta);
      try { const n = ta.value.length; ta.setSelectionRange(n, n); } catch (e) { /* ignore */ }
    }
    // The bubble's Esc handler (a window capture listener — it runs before
    // this box ever sees the key) hands the Esc over as this event.
    const h = function () { cancelRef.current(true); };
    ta.addEventListener("wb-cancel-edit", h);
    return function () { ta.removeEventListener("wb-cancel-edit", h); };
  }, [editing]);
  useEffect(function () { if (editing) autosize(taRef.current, max); }, [editing, draft, max]);
  // Closed / hidden / unmounted mid-edit: what was typed becomes the note now
  // (an edit over the limit stays open in the store instead — nothing lost).
  // Checked a tick later, so React's dev double-mount isn't taken for a close.
  useEffect(function () {
    aliveRef.current = true;
    return function () {
      aliveRef.current = false;
      if (!shownRef.current) return;
      setTimeout(function () { if (!aliveRef.current) store.finishEdit(id); }, 0);
    };
  }, [store, id]);

  const tooLong = editing && draft.length > WB_MAX;
  const blank = editing && !draft.trim();
  // Text in the box that is not the note and isn't on its way to it — an
  // edit brought back after a sign-out / reload. Done saves it.
  const unsaved = editing && !tooLong && !blank && !pend.queued && draft !== note.body;
  return (<div data-wb-note={id} data-pinned={note.pinned ? "" : undefined}
    style={{ position: "relative", background: C.cream, border: "1px solid " + C.cream2, borderTop: note.pinned ? "3px solid " + C.clay : "1px solid " + C.cream2, borderRadius: 10, padding: compact ? "10px 12px 8px" : "12px 14px 10px", boxShadow: "0 1px 0 rgba(11,42,74,0.05), 0 6px 14px rgba(11,42,74,0.06)", display: "flex", flexDirection: "column", gap: 6, minWidth: 0, boxSizing: "border-box" }}>
    {editing
      ? <textarea ref={taRef} value={draft} data-wb-editor={id} aria-label="Edit note" aria-invalid={tooLong || undefined}
          onChange={onChange} onKeyDown={onKey} onBlur={function () { finish(false); }}
          style={{ width: "100%", boxSizing: "border-box", resize: "none", border: "1px solid " + (tooLong ? C.red : C.border), borderRadius: 8, background: C.card, color: C.text, fontSize: compact ? 13.5 : 14, lineHeight: 1.5, fontFamily: FONT, padding: "7px 9px", minHeight: 60, maxHeight: max, overflowY: "auto", outline: "none" }} />
      : <div ref={textRef} data-wb-text="" role="button" tabIndex={0} title="Click to edit"
          onClick={startEdit} onKeyDown={function (e) { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); startEdit(); } }}
          style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", fontSize: compact ? 13.5 : 14, lineHeight: 1.5, color: C.text, cursor: "text", maxHeight: max, overflowY: "auto", borderRadius: 6 }}>{note.body}</div>}
    {tooLong && <div role="alert" data-wb-too-long="" style={{ fontSize: 11, color: C.red, fontWeight: 700, lineHeight: 1.4 }}>Too long — {fmtN(draft.length)} / {fmtN(WB_MAX)} characters. Not saved: the note keeps its last saved text until this is {fmtN(WB_MAX)} or fewer. What you typed stays here (on this device) — Esc cancels the edit.</div>}
    {blank && <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.4 }}>An empty note isn't saved — left empty, it goes back to how it was. Erase removes a note.</div>}
    {unsaved && <div data-wb-edit-unsaved="" style={{ fontSize: 11, color: C.clayDeep, fontWeight: 700, lineHeight: 1.4 }}>Not saved yet — Done saves it; Esc puts the note back.</div>}
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 10.5, color: C.textDim }}>
      {note.pinned && <span style={{ color: C.clayDeep, fontWeight: 800 }}>Pinned</span>}
      <span data-wb-when="">{whenLabel(note, now)}</span>
      {failed && <span data-wb-unsaved="" title="This note couldn't be saved to your database yet. It's kept on this device and is sent again when you come back to this window." style={{ color: C.red, fontWeight: 800 }}>Not saved — will retry</span>}
      <span style={{ flex: 1 }} />
      {/* While editing, a press on these keeps the cursor in the box (no blur
          → no re-layout under the pointer before the click lands). */}
      <button type="button" style={actBtn} onMouseDown={keepFocus} onClick={function () { store.togglePin(id); }} aria-label={(note.pinned ? "Unpin" : "Pin") + " this note"}>{note.pinned ? "Unpin" : "Pin"}</button>
      {editing
        ? <button type="button" style={actBtn} onMouseDown={keepFocus} onClick={function () { finish(true); }} aria-label="Done editing this note" disabled={tooLong}>Done</button>
        : <button type="button" style={actBtn} onClick={startEdit} aria-label="Edit this note">Edit</button>}
      <button type="button" style={{ ...actBtn, color: C.red }} onMouseDown={keepFocus} onClick={function () { onErase(id); }} aria-label="Erase this note">Erase</button>
    </div>
  </div>);
}

// ═══════ Board-level notes (where the notes are saved) ═══════
function SaveNotice({ staff }) {
  const mode = useWhiteboard(staff, function (s) { return s.mode; });
  const reason = useWhiteboard(staff, function (s) { return s.localReason; });
  const loadError = useWhiteboard(staff, function (s) { return s.loadError; });
  const lsError = useWhiteboard(staff, function (s) { return s.lsError; });
  const box = function (text, warn, key) {
    return <div key={key} data-wb-notice={key} style={{ fontSize: 11, lineHeight: 1.45, color: warn ? C.red : C.textMuted, background: warn ? C.redBg : C.surfaceAlt, border: "1px solid " + (warn ? C.red + "30" : C.border), borderRadius: 8, padding: "7px 10px" }}>{text}</div>;
  };
  const out = [];
  if (mode === "local" && reason === "no_table") out.push(box("Saved on this device only — the whiteboard's database table isn't set up yet (" + WB_SQL_FILE + "). Your notes are uploaded once it is.", false, "no-table"));
  if (mode === "local" && reason === "offline") out.push(box("Saved on this device only — no database connection is configured.", false, "offline"));
  if (loadError && mode !== "local") out.push(box("Couldn't reach the database — showing the copy on this device. It tries again when you come back to this window.", false, "load-error"));
  if (lsError && mode === "local") out.push(box("Couldn't save on this device (browser storage is full or blocked) — notes marked “Not saved” are only in this tab.", true, "ls-error"));
  return out.length ? <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>{out}</div> : null;
}

// ═══════ The notes ═══════
// variant "panel" (Command Center): sticky notes in a responsive grid.
// variant "dock" (the bubble): one column.
export function WhiteboardNotes({ staff, variant }) {
  const grid = variant === "panel";
  const compact = !grid;
  const store = getWhiteboardStore(staff);
  const active = useWhiteboard(staff, function (s) { return s.active; });
  const erased = useWhiteboard(staff, function (s) { return s.erased; });
  const status = useWhiteboard(staff, function (s) { return s.status; });
  const now = useNow();
  const [undo, setUndo] = useState(null);              // the note just erased
  const [showErased, setShowErased] = useState(false);
  useEffect(function () {
    if (!undo) return undefined;
    const t = setTimeout(function () { setUndo(null); }, UNDO_MS);
    return function () { clearTimeout(t); };
  }, [undo]);
  // Back on the board some other way (restored below, another device): no Undo.
  useEffect(function () { if (undo && !erased.some(function (n) { return n.id === undo; })) setUndo(null); }, [undo, erased]);
  const onErase = useCallback(function (id) { if (store.erase(id)) setUndo(id); }, [store]);
  const doUndo = function () { if (undo) store.restore(undo); setUndo(null); };

  return (<div data-wb-notes={variant || "dock"}>
    <SaveNotice staff={staff} />
    {undo && <div role="status" data-wb-undo="" style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, fontWeight: 700, color: C.cream, background: C.navyDeep, borderRadius: 9, padding: "7px 12px", marginBottom: 10 }}>
      <span>Erased</span>
      <span style={{ flex: 1 }} />
      <button type="button" onClick={doUndo} style={{ ...linkBtn, color: C.claySoft }}>Undo</button>
    </div>}
    {active.length === 0
      ? <div data-wb-empty="" style={{ padding: "12px 14px", fontSize: 12, color: C.textDim, border: "1px dashed " + C.border, borderRadius: 8, lineHeight: 1.5 }}>Nothing on the board. Jot anything here — it stays until you erase it.</div>
      : <div data-wb-list="" style={grid
          ? { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(min(230px,100%),1fr))", gap: 12, alignItems: "start" }
          : { display: "flex", flexDirection: "column", gap: 8 }}>
          {active.map(function (n) { return <NoteCard key={n.id} staff={staff} note={n} failed={status[n.id] === "failed"} compact={compact} now={now} onErase={onErase} />; })}
        </div>}
    {erased.length > 0 && <div data-wb-erased="" style={{ marginTop: 12 }}>
      <button type="button" aria-expanded={showErased} onClick={function () { setShowErased(function (v) { return !v; }); }}
        style={{ border: "none", background: "transparent", padding: "4px 0", color: C.textMuted, fontSize: 11.5, fontWeight: 800, cursor: "pointer", fontFamily: FONT }}>
        {showErased ? "▾" : "▸"} Recently erased ({erased.length})
      </button>
      {showErased && <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
        {erased.slice(0, ERASED_SHOWN).map(function (n) {
          return (<div key={n.id} data-wb-erased-note={n.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "7px 10px", borderRadius: 8, background: C.surfaceAlt, border: "1px solid " + C.border }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: C.textMuted, whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: 54, overflow: "hidden" }}>{n.body}</div>
              <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>erased {relTime(n.erasedAt, now)}{status[n.id] === "failed" ? " · " : ""}{status[n.id] === "failed" && <span data-wb-unsaved="" style={{ color: C.red, fontWeight: 800 }}>Not saved — will retry</span>}</div>
            </div>
            <button type="button" style={actBtn} onClick={function () { store.restore(n.id); }} aria-label="Restore this note">Restore</button>
          </div>);
        })}
        {erased.length > ERASED_SHOWN && <div style={{ fontSize: 10.5, color: C.textDim }}>Showing the {ERASED_SHOWN} most recently erased.</div>}
      </div>}
    </div>}
  </div>);
}

// ═══════ Command Center panel ═══════
// Re-reads the board when the Command Center shows it (another device may
// have written since), and sends any half-saved edit when it goes away.
export function WhiteboardPanel({ staff }) {
  const who = staff || "reece";
  const store = getWhiteboardStore(who);
  const count = useWhiteboard(who, function (s) { return s.active.length; });
  useEffect(function () { store.refresh(); return function () { store.flush(); }; }, [store]);
  return (<section id="cc-whiteboard" data-whiteboard-panel="" data-testid="whiteboard-panel" aria-label="Whiteboard"
    style={{ background: C.card, borderRadius: 16, border: "1px solid " + C.border, padding: "18px 22px", marginBottom: 24, scrollMarginTop: 90 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 4, flexWrap: "wrap" }}>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: .2 }}>🗒️ Whiteboard</h3>
      {count > 0 && <span style={{ fontSize: 11, fontWeight: 800, color: C.clayDeep, background: C.clay + "1F", padding: "1px 9px", borderRadius: 10 }}>{count} {count === 1 ? "note" : "notes"}</span>}
    </div>
    <div style={{ fontSize: 10.5, color: C.textDim, marginBottom: 12 }}>Misc notes in your own words — not a to-do list. They stay on the board until you erase them. The same notes are in the bubble at the bottom-left of every other screen.</div>
    <div style={{ maxWidth: 640, marginBottom: 14 }}><WhiteboardAdd staff={who} /></div>
    <WhiteboardNotes staff={who} variant="panel" />
  </section>);
}
